import { createHtmlElement } from "../../helpers/dom-manipulation/dom-manipulation";

/**
 * Migrate an array of HTML string objects into a swiper wrapper element.
 *
 * @param {string[]} objects - Array of HTML strings representing slides.
 * @param {Element} element - The swiper wrapper element to append slides to.
 * @returns {void}
 */
export function migrateSwiper(objects, element) {
    objects.forEach((obj) => {
        const slide = createHtmlElement(obj);
        element.appendChild(slide);
    });
}

/**
 * Extract the numeric data ID from a swiper slide element.
 *
 * @param {Element | null | undefined} slide - The slide element with a data-id attribute.
 * @returns {string | null} The extracted ID portion after the hyphen, or null if unavailable.
 */
export function getSlideDataId(slide) {
    if (
        !slide ||
        !(/** @type {HTMLElement} */ (slide).dataset) ||
        !(/** @type {HTMLElement} */ (slide).dataset.id)
    ) {
        return null;
    }
    return /** @type {string} */ (
        /** @type {HTMLElement} */ (slide).dataset.id
    ).split("-")[1];
}

/**
 * Get the number of loaded slides in a swiper instance.
 *
 * @param {{ _swiper: { slides: ArrayLike<any> } }} swiper - A swiperScroll or swiperSlider instance.
 * @returns {number} The number of slides currently loaded.
 */
export function getNumberOfLoadedSlides(swiper) {
    return swiper._swiper.slides.length;
}

/**
 * Get the data ID of the most recently added (last) slide in a swiper instance.
 *
 * @param {{ _swiper: { slides: ArrayLike<any> } }} swiper - A swiperScroll or swiperSlider instance.
 * @returns {string | null} The data ID of the last slide, or null if unavailable.
 */
export function getMostRecentSlideId(swiper) {
    const slide = swiper._swiper.slides[getNumberOfLoadedSlides(swiper) - 1];
    return getSlideDataId(slide);
}

/**
 * Get all fully visible slides, optionally including the last partially visible slide
 * when the swiper has reached the end.
 *
 * @param {[string, string]} selectors - A tuple of [fullyVisibleSelector, visibleSelector].
 * @param {boolean} reachedEnd - Whether the swiper has reached its end position.
 * @returns {Element[]} An array of fully visible slide elements.
 */
export function getFullyVisibleSlides([fullyVisible, visible], reachedEnd) {
    const fullyVisibleSlides = Array.from(
        document.querySelectorAll(fullyVisible),
    );

    if (reachedEnd) {
        const visibleSlides = document.querySelectorAll(visible);
        const lastSlide = visibleSlides[visibleSlides.length - 1];

        if (lastSlide && !fullyVisibleSlides.includes(lastSlide)) {
            fullyVisibleSlides.push(lastSlide);
        }
    }
    return fullyVisibleSlides;
}
